import React, { useMemo } from 'react';
import { SimpleGrid, Stack } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('managed-sales-age-vs-returns');

export default function ManagedSalesAgeVsReturns() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const age = useNexusQuery('initiative-age-vs-returns', window);

  const data = useMemo(() => {
    const rows = age.rows.map((r) => ({
      bucket: field(r, 'AgeBucket'),
      sortOrder: num(r, 'SortOrder'),
      lines: num(r, 'Lines'),
      revenue: num(r, 'Revenue'),
      marginPct: num(r, 'GrossMarginPct'),
      returned: num(r, 'ReturnedLines'),
      returnRate: num(r, 'ReturnRatePct'),
    }));
    rows.sort((a, b) => a.sortOrder - b.sortOrder);
    const totalLines = rows.reduce((s, d) => s + d.lines, 0);
    return rows.map((d) => ({
      ...d,
      salesPct: totalLines ? (d.lines / totalLines) * 100 : 0,
    }));
  }, [age.rows]);

  const summary = useMemo(() => {
    if (!data.length) return null;
    const first = data[0];
    const last = data[data.length - 1];
    return {
      first,
      last,
      multiple: first.returnRate ? last.returnRate / first.returnRate : 0,
      totalReturned: data.reduce((s, d) => s + d.returned, 0),
    };
  }, [data]);

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        <QueryState isLoading={age.isLoading} error={age.error} isEmpty={!data.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label={`Return rate, ${summary?.first?.bucket ?? 'newest'}`}
              value={fmtPct(summary?.first?.returnRate ?? 0)}
              sub="Newest stock — the baseline"
              color={c.positive}
            />
            <StatTile
              label={`Return rate, ${summary?.last?.bucket ?? 'oldest'}`}
              value={fmtPct(summary?.last?.returnRate ?? 0)}
              sub={`${(summary?.multiple ?? 0).toFixed(1)}x the baseline`}
              color={c.negative}
            />
            <StatTile
              label="Sales in the oldest bucket"
              value={fmtPct(summary?.last?.salesPct ?? 0)}
              sub={`${fmtInt(summary?.last?.lines ?? 0)} lines, ${fmtMoney(summary?.last?.revenue ?? 0)}`}
              color={c.series[3]}
            />
            <StatTile
              label="Returned lines"
              value={fmtInt(summary?.totalReturned ?? 0)}
              sub="Linked back to their originating invoice"
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Sales mix and return rate by inventory age"
            caption="The whole question in one chart. A return line that climbs with age means aged stock costs more than its carrying cost — and moving it earlier at a lower price becomes the cheaper option, not the desperate one."
            height={320}
          >
            <QueryState isLoading={age.isLoading} error={age.error} isEmpty={!data.length}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="bucket" stroke={c.axis} fontSize={11} interval={0} />
                  <YAxis
                    yAxisId="left"
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => `${Math.round(v)}%`}
                    width={48}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => `${v.toFixed(0)}%`}
                    width={48}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => fmtPct(v)} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend iconType="circle" />
                  <Bar
                    yAxisId="left"
                    dataKey="salesPct"
                    name="Share of managed sales"
                    fill={c.series[0]}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="returnRate"
                    name="Return rate"
                    stroke={c.negative}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Margin rate by inventory age at sale"
            caption="The other half of the cost of holding. Where margin erodes and returns rise together, the true cost of aged stock is larger than either number suggests on its own."
            height={320}
          >
            <QueryState isLoading={age.isLoading} error={age.error} isEmpty={!data.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="bucket" stroke={c.axis} fontSize={11} interval={0} />
                  <YAxis
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => `${Math.round(v)}%`}
                    width={48}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => fmtPct(v)} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar
                    dataKey="marginPct"
                    name="Gross margin rate"
                    fill={c.series[1]}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>
      </Stack>
    </KpiPage>
  );
}
